import React from "react";
import styled from "styled-components";

type TopupStatus = "success" | "cancel" | "error";

type TopupStatusModalProps = {
  open: boolean;
  status: TopupStatus;
  message?: string | null;
  onClose: () => void;
};

export const TopupStatusModal: React.FC<TopupStatusModalProps> = ({
  open,
  status,
  message,
  onClose,
}) => {
  if (!open) return null;

  const isSuccess = status === "success";
  const isCancel = status === "cancel";

  const title = isSuccess
    ? "Top-up successful"
    : isCancel
    ? "Top-up cancelled"
    : "Top-up failed";

  const description =
    message ||
    (isSuccess
      ? "Your balance has been successfully topped up."
      : isCancel
      ? "You cancelled the payment. No money has been charged."
      : "Something went wrong while processing your payment. Please try again or contact support.");

  return (
    <Overlay onClick={onClose}>
      <Dialog
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <IconWrapper $status={status}>
          {isSuccess ? (
            <CheckIcon viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="11" />
              <path
                d="M7.5 12.5L10.5 15.5L16.5 9"
                fill="none"
              />
            </CheckIcon>
          ) : (
            <CrossIcon viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="11" />
              <path d="M8 8L16 16" />
              <path d="M16 8L8 16" />
            </CrossIcon>
          )}
        </IconWrapper>
        <Title>{title}</Title>
        <Text>{description}</Text>
        <ButtonRow>
          <OkButton type="button" onClick={onClose}>
            OK
          </OkButton>
        </ButtonRow>
      </Dialog>
    </Overlay>
  );
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

const Dialog = styled.div`
  width: 360px;
  max-width: 90vw;
  border-radius: 24px;
  background: #ffffff;
  padding: 22px 22px 18px 22px;
  box-shadow: 0 26px 70px rgba(15, 23, 42, 0.4);
  display: flex;
  flex-direction: column;
  gap: 12px;
  text-align: center;
`;

const IconWrapper = styled.div<{ $status: TopupStatus }>`
  width: 60px;
  height: 60px;
  margin: 0 auto 4px auto;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $status }) =>
    $status === "success"
      ? "rgba(34, 197, 94, 0.12)"
      : "rgba(248, 113, 113, 0.12)"};
`;

const CheckIcon = styled.svg`
  width: 40px;
  height: 40px;

  circle {
    fill: #22c55e;
  }

  path {
    stroke: #ffffff;
    stroke-width: 2.2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

const CrossIcon = styled.svg`
  width: 40px;
  height: 40px;

  circle {
    fill: #f97373;
  }

  path {
    stroke: #ffffff;
    stroke-width: 2.2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
`;

const Text = styled.div`
  font-size: 13px;
  color: #4b5563;
`;

const ButtonRow = styled.div`
  margin-top: 6px;
  display: flex;
  justify-content: center;
`;

const OkButton = styled.button`
  min-width: 120px;
  border-radius: 999px;
  border: none;
  padding: 9px 18px;
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  background: linear-gradient(90deg, #7987ff 0%, #ff7b7d 100%);
  cursor: pointer;
  box-shadow: 0 8px 18px rgba(248, 113, 113, 0.35);
  transition: transform 0.1s ease, box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-0.5px);
    box-shadow: 0 10px 24px rgba(248, 113, 113, 0.45);
  }
`;


